// Texts of the Device Details 1NCE SIM actions (documentation/onence.md "SIM actions"): what each
// action does, which control each action pair shows, and how outcomes and failures are reported.
// Kept apart from the components (OnenceSimActions.tsx) so both can be tested and the components
// module exports components only.
import {
  onenceErrorText, OnenceActionError, type OnenceActionPanel, type OnenceActionRecord, type OnenceActionResult, type OnenceSimAction,
} from '../../services/onence';

export const actionMeta: Record<OnenceSimAction, { label: string; title: string; effects: string[]; danger: boolean }> = {
  disable: {
    label: 'Disable SIM',
    title: 'Disable this SIM?',
    effects: [
      'The radio loses mobile data at once: PTT, location and remote management over the mobile network stop.',
      'The SIM stays disabled until it is enabled again (here or in the 1NCE portal).',
    ],
    danger: true,
  },
  enable: {
    label: 'Enable SIM',
    title: 'Enable this SIM?',
    effects: ['1NCE enables the SIM; the radio uses mobile data again once it attaches to the network.'],
    danger: false,
  },
  imei_lock: {
    label: 'Lock to current IMEI',
    title: 'Lock this SIM to the device it is in now?',
    effects: [
      '1NCE binds the SIM to the device it is in now (1NCE uses the IMEISV). Any other device is refused.',
      'An active data session is dropped and the radio reconnects at once.',
      'Not documented by 1NCE: whether a radio firmware update that changes the IMEISV software version affects the lock.',
    ],
    danger: true,
  },
  imei_unlock: {
    label: 'Remove IMEI lock',
    title: 'Remove the IMEI lock?',
    effects: ['The SIM can then be used in any device.'],
    danger: false,
  },
  reset: {
    label: 'Reset connectivity',
    title: 'Reset this SIM’s connectivity?',
    effects: [
      '1NCE briefly deactivates and reactivates the SIM: the radio is detached and re-attaches, so PTT and data are interrupted for a short time.',
      '1NCE queues the reset and performs it asynchronously; RadioOS can report only that it was queued, not when it completed.',
    ],
    danger: true,
  },
};

/** One button of the actions panel: the action it runs (null: none, the button stays disabled), its
 *  label, whether the server offers it now, and the reason when it does not. */
export interface SimActionControl {
  key: 'sim' | 'imei_lock' | 'reset';
  action: OnenceSimAction | null;
  label: string;
  available: boolean;
  reason?: string;
  danger: boolean;
}

/** The panel's buttons: one for the SIM status (disable or enable), one for the IMEI lock (lock or
 *  remove) and the connectivity reset. The pair button runs the action that changes the state the
 *  server's expected view reports, and only if the server offers that action. It is never enabled
 *  when that state is unknown, the server lacks the option, or the opposite action is offered (the
 *  panel contradicts itself); the server re-checks everything anyway. */
export function simActionControls(panel: OnenceActionPanel): SimActionControl[] {
  const exp = panel.expected;
  const find = (a: OnenceSimAction) => panel.options.find((o) => o.action === a);
  const pair = (key: 'sim' | 'imei_lock', on: OnenceSimAction, off: OnenceSimAction, current: OnenceSimAction | null, unknownLabel: string, unknownReason: string): SimActionControl | null => {
    const a = find(on), b = find(off);
    if (!a && !b) return null;
    if (!current) {
      return { key, action: null, label: unknownLabel, available: false, reason: a?.reason ?? b?.reason ?? unknownReason, danger: false };
    }
    const o = find(current);
    const other = current === on ? b : a;
    const m = actionMeta[current];
    if (!o) return { key, action: current, label: m.label, available: false, reason: 'the server does not offer this action now', danger: m.danger };
    if (other?.available) {
      return { key, action: current, label: m.label, available: false, reason: 'the server’s view of this SIM is inconsistent; reload the SIM', danger: m.danger };
    }
    return { key, action: current, label: m.label, available: o.available, reason: o.available ? undefined : o.reason, danger: m.danger };
  };
  const status = exp?.sim_status === 'Enabled' ? 'disable' : exp?.sim_status === 'Disabled' ? 'enable' : null;
  const lock = exp?.imei_lock === false ? 'imei_lock' : exp?.imei_lock === true ? 'imei_unlock' : null;
  const out: (SimActionControl | null)[] = [
    pair('sim', 'disable', 'enable', status, 'Disable / enable SIM', `the SIM status is unknown (${exp?.sim_status || 'none'})`),
    pair('imei_lock', 'imei_lock', 'imei_unlock', lock, 'IMEI lock', '1NCE did not report the IMEI lock'),
  ];
  const r = find('reset');
  if (r) out.push({ key: 'reset', action: 'reset', label: actionMeta.reset.label, available: r.available, reason: r.available ? undefined : r.reason, danger: actionMeta.reset.danger });
  return out.filter((c): c is SimActionControl => c !== null);
}

const cat = (c?: string) => (c ? onenceErrorText[c] ?? c : 'no details');

/** The outcome of an action the server sent (or tried to send) to 1NCE. */
export function outcomeText(r: Pick<OnenceActionResult, 'outcome' | 'category' | 'retry_after_seconds' | 'verify_error'>): string {
  const retry = r.retry_after_seconds ? ` Try again in ${r.retry_after_seconds} s.` : '';
  switch (r.outcome) {
    case 'confirmed':
      return 'Done: 1NCE accepted the change and a fresh read confirms it.';
    case 'pending':
      return `1NCE accepted the change, but a fresh read does not show it yet${r.verify_error ? ` (re-read: ${cat(r.verify_error)})` : ''}. Check again shortly.`;
    case 'queued':
      return '1NCE queued the connectivity reset. It runs asynchronously; RadioOS cannot see when it completes.';
    case 'rejected':
      return `1NCE refused the request (${cat(r.category)}). Nothing was changed.${retry}`;
    case 'not_sent':
      return `The request was not sent (${cat(r.category)}). Nothing was changed.${retry}`;
    case 'uncertain':
      return `Outcome unknown (${cat(r.category)}): 1NCE may or may not have applied it. RadioOS did not retry. Check the SIM before trying again.`;
    default:
      return `Unexpected outcome: ${r.outcome}`;
  }
}

/** A failed request. A coded server answer is a refusal before anything was sent to 1NCE; without a
 *  code (lost connection, proxy timeout) the server may still have run the action. */
export function actionErrorText(err: unknown): string {
  if (err instanceof OnenceActionError && err.code) return `${err.message} (${err.code}). Nothing was changed.`;
  const msg = err instanceof Error ? err.message : String(err);
  return `${msg}. The answer was lost, so the action may have run: check the SIM before trying again.`;
}

/** The latest action on this SIM as the audit records it. */
export function lastActionText(l: OnenceActionRecord): string {
  const what = actionMeta[l.action]?.label ?? l.action;
  const who = l.actor ? ` by ${l.actor}` : '';
  const when = new Date(l.started_at).toLocaleString();
  switch (l.state) {
    case 'in_progress':
      return `${what}${who} (${when}) is still running.`;
    case 'no_outcome':
      return `${what}${who} (${when}): no outcome was recorded; the result is unknown.`;
    default:
      return `${what}${who} (${when}, API ${l.api_version ?? '?'}): ${outcomeText({ outcome: l.state, category: l.code })}`;
  }
}
